import React, { useEffect, useState } from 'react';
import { getTemplateDefinitions } from '../../services/StrategyTemplatesService';
import { processError } from '../../services/ServiceUtils';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import { getEmptyMessage, getErrorMessage, getSuccessMessage } from '../../utils/MessageUtils';
import StrategyTemplateModal from './StrategyTemplateModal';
import GenerateStrategyModal from './GenerateStrategyModal';
import RefineStrategyModal from './RefineStrategyModal';

// Refinável por IA: tem blueprint próprio (gerado) OU é um starter que a IA sabe expressar como blueprint.
const REFINABLE_BUILDERS = ['EMA_CROSSOVER', 'DCA_DIP', 'BLUEPRINT'];
const isRefinable = t => !!(t && (t.blueprint || REFINABLE_BUILDERS.includes(t.builderKey)));

// Admin: edita a camada DECLARATIVA dos templates no DB (valores/presets/labels/hints/enabled), sem
// deploy. A LÓGICA (builders) fica em código — não editável aqui. Ver project_templates_in_db.
function StrategyTemplates() {

    const [notification, setNotification] = useState(getEmptyMessage());
    const [templates, setTemplates] = useState([]);
    const [editing, setEditing] = useState(null);
    const [refining, setRefining] = useState(null);
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        getTemplateDefinitions()
            .then(rows => setTemplates(rows || []))
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }, [reload]);

    function onEditClick(tpl) {
        setEditing(tpl);
    }

    function onSaved() {
        setNotification(getSuccessMessage('Template atualizado!'));
        setReload(v => v + 1);
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4'>
                    <div className='d-block mb-4 mb-md-0'>
                        <h2 className='h4'>Templates de Estratégia</h2>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <button className="btn btn-sm btn-success" title="Criar uma estratégia nova a partir de uma descrição, usando IA"
                            data-bs-toggle="modal" data-bs-target="#modalGenerateStrategy">
                            ✨ Gerar por IA
                        </button>
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-12">
                        <button className="btn btn-sm btn-outline-info" onClick={() => setShowHowItWorks(v => !v)}>
                            {showHowItWorks ? 'Ocultar explicação' : 'Como funciona?'}
                        </button>
                        {showHowItWorks && (
                            <div className="card border-info mt-2">
                                <div className="card-body py-2 text-info">
                                    <p className="mb-1">Aqui você ajusta os <strong>templates de estratégia</strong> que os traders usam ao criar uma estratégia — <strong>sem precisar de deploy</strong>.</p>
                                    <p className="mb-1">É possível editar <strong>valores</strong>: defaults, presets (🛡️ Conservador / ⚖️ Moderado / 🚀 Agressivo), rótulos, dicas, descrição, ativar/desativar e a ordem.</p>
                                    <p className="mb-1">Não é possível mudar a <strong>lógica</strong> da estratégia (as condições de compra/venda) nem adicionar/remover campos — isso vive no código (segurança).</p>
                                    <p className="mb-0">Editar um template <strong>não altera estratégias já criadas</strong> — vale para as próximas.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th className="border-gray-200">#</th>
                                <th className="border-gray-200">Template</th>
                                <th className="border-gray-200">Mercado</th>
                                <th className="border-gray-200">Dificuldade</th>
                                <th className="border-gray-200">Campos</th>
                                <th className="border-gray-200">Status</th>
                                <th className="border-gray-200">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map(t => (
                                <tr key={t.id} className="align-middle">
                                    <td>{t.sortOrder}</td>
                                    <td>
                                        <div className="fw-bold">{t.name}</div>
                                        <small className="text-muted">{t.id}</small>
                                        {!t.builderKnown && <span className="badge bg-danger ms-2" title="Sem builder em código">órfão</span>}
                                    </td>
                                    <td>{t.market}</td>
                                    <td>{t.difficulty || '-'}</td>
                                    <td>{(t.configFields || []).length}</td>
                                    <td>
                                        <span className={t.enabled ? 'text-success' : 'text-danger'}>
                                            {t.enabled ? 'Ativo' : 'Desativado'}
                                        </span>
                                    </td>
                                    <td>
                                        <button className="btn btn-primary btn-xs me-1" title="Editar template"
                                            data-bs-toggle="modal" data-bs-target="#modalStrategyTemplate"
                                            onClick={() => onEditClick(t)}>
                                            <svg className="icon icon-xs" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        {isRefinable(t) && (
                                            <button className="btn btn-outline-info btn-xs" title="Refinar com IA"
                                                data-bs-toggle="modal" data-bs-target="#modalRefineStrategy"
                                                onClick={() => setRefining(t)}>🔧</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
            <StrategyTemplateModal data={editing} onSaved={onSaved} />
            <GenerateStrategyModal onSaved={() => { setNotification(getSuccessMessage('Estratégia gerada e salva como template!')); setReload(v => v + 1); }} />
            <RefineStrategyModal data={refining} onSaved={(msg) => { setNotification(getSuccessMessage(msg || 'Template refinado!')); setReload(v => v + 1); }} />
        </React.Fragment>);
}

export default StrategyTemplates;
